import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaEye,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../components/common/Header";
import WebinarService from "../../services/webinarService";

const WebinarManagement = () => {
  const navigate = useNavigate();
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [registrations, setRegistrations] = useState([]);

  useEffect(() => {
    loadWebinars();
  }, []);

  const loadWebinars = async () => {
    try {
      setLoading(true);
      const response = await WebinarService.adminGetAllWebinars();
      setWebinars(response.data || []);
    } catch (error) {
      toast.error("Failed to load webinars");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await WebinarService.adminDeleteWebinar(id);
        toast.success("Webinar deleted successfully");
        loadWebinars();
      } catch (error) {
        toast.error("Failed to delete webinar");
      }
    }
  };

  const handleViewRegistrations = async (webinar) => {
    try {
      const response = await WebinarService.adminGetRegistrations(webinar.id);
      setRegistrations(response.data || []);
      setSelectedWebinar(webinar);
      setShowRegistrationsModal(true);
    } catch (error) {
      toast.error("Failed to load registrations");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "upcoming":
        return <Badge bg="success">📅 Upcoming</Badge>;
      case "live":
        return <Badge bg="danger">🔴 Live</Badge>;
      case "completed":
        return <Badge bg="secondary">📹 Completed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    return time.substring(0, 5);
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading webinars...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Container fluid className="p-4" >
        <Card className="shadow-sm">
          <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaEye className="me-2" /> Webinar Management
            </h5>
            <Button 
              variant="light" 
              size="sm" 
              onClick={() => navigate("/admin/webinars/create")}
            >
              <FaPlus className="me-1" /> Create Webinar
            </Button>
          </Card.Header>
          <Card.Body>
            {webinars.length === 0 ? (
              <Alert variant="info" className="text-center">
                <FaEye size={40} className="mb-3" />
                <h5>No Webinars Created Yet</h5>
                <p>Click the "Create Webinar" button to add your first webinar.</p>
              </Alert>
            ) : (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Instructor</th>
                    <th>Date & Time</th>
                    <th>Status</th>
                    <th>Registrations</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {webinars.map((webinar) => (
                    <tr key={webinar.id}>
                      <td>{webinar.id}</td>
                      <td>
                        <strong>{webinar.title}</strong>
                        {webinar.short_description && (
                          <>
                            <br />
                            <small className="text-muted">
                              {webinar.short_description.substring(0, 50)}...
                            </small>
                          </>
                        )}
                      </td>
                      <td>{webinar.instructor_name}</td>
                      <td>
                        <div>{formatDate(webinar.webinar_date)}</div>
                        <small className="text-muted">{formatTime(webinar.webinar_time)}</small>
                      </td>
                      <td>{getStatusBadge(webinar.status)}</td>
                          <td>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleViewRegistrations(webinar)}
                        >
                          <FaUsers className="me-1" />
                          {webinar.total_registrations || 0} Registrations
                        </Button>
                       </td>
                       <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => navigate(`/admin/webinars/edit/${webinar.id}`)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(webinar.id, webinar.title)}
                        >
                          <FaTrash />
                        </Button>
                       </td>
                     </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Registrations Modal */}
      <Modal show={showRegistrationsModal} onHide={() => setShowRegistrationsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUsers className="me-2" />
            Registrations for: {selectedWebinar?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {registrations.length === 0 ? (
            <Alert variant="info">No registrations yet for this webinar.</Alert>
          ) : (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Registered On</th>
                  <th>Attended</th>
                 </tr>
              </thead>
              <tbody>
                {registrations.map((reg, index) => (
                  <tr key={reg.id}>
                    <td>{index + 1}</td>
                    <td>{reg.name}</td>
                    <td>{reg.email}</td>
                    <td>{reg.phone || "N/A"}</td>
                    <td>{new Date(reg.registered_at).toLocaleString()}</td>
                    <td>
                      <Badge bg={reg.attended ? "success" : "warning"}>
                        {reg.attended ? "Attended" : "Pending"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRegistrationsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WebinarManagement;